import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import type { BusinessCertUploadResult, ValidateResponse } from '@/api/repository/types/types';
import { FORM_COLOR as C } from '@/pages/Seminar/components/form/formStyles';
import { claudeTokens } from '@/styles/claudeTokens';
import { designSystem } from '@/styles/theme';
import { compressLicenseImage } from '@/utils/imageUpload';
import { BUSINESS_CERT_MIME, businessCertFileProblem } from '@/utils/clinicFields';

/**
 * 사업자등록증 사진 칸 — 가입·치과 정보 수정이 같이 쓴다. 모양은 면허 인증 사진 올리기(LicenseVerificationPage)와 같다
 * (점선 버튼 → 미리보기·파일 이름·[삭제]). 고르면 바로 압축해 비공개로 올리고 토큰을 [onChange] 로 넘긴다 — 저장·가입 요청이 토큰을 함께 보낸다.
 */
interface BusinessCertPhotoFieldProps {
    /** 사진을 올리고 토큰을 받는 함수(가입: 이메일 포함 공개 API, 치과 정보: 로그인 API) */
    upload: (file: File) => Promise<ValidateResponse<BusinessCertUploadResult>>;
    /** 올린 사진 토큰(지우면 null) */
    onChange: (token: string | null) => void;
    /** 칸 아래 빨간 문구 */
    error?: string;
    /** 전에 올린 사진이 있으면 그 안내(치과 정보 수정) */
    existingNote?: string;
    /** 올리기 전에 먼저 해야 할 일(이메일 인증 등) — 있으면 누를 때 이 문구만 보이고 파일 창을 열지 않는다 */
    blockedReason?: string;
    onBlocked?: () => void;
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PickButton = styled.button`
    width: 100%;
    height: 52px;
    border: 1px dashed ${C.border};
    border-radius: 12px;
    background: #fff;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    color: ${C.sub};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        border-color: ${designSystem.colors.primary};
        color: ${designSystem.colors.primaryDark};
    }

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
`;

const AttachedBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${C.line};
    border-radius: 12px;
    background: ${C.bg};
`;

const AttachedRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    color: ${claudeTokens.color.text2};
    word-break: break-all;
`;

const RemoveButton = styled.button`
    flex-shrink: 0;
    height: 44px;
    padding: 0 16px;
    border: 1px solid ${C.border};
    border-radius: 12px;
    background: #fff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: ${C.sub};
    cursor: pointer;
`;

const Preview = styled.img`
    width: 100%;
    max-height: 260px;
    object-fit: contain;
    border-radius: 8px;
    background: ${claudeTokens.color.surface};
`;

const Help = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: ${C.muted};
    word-break: keep-all;
`;

const ErrorText = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${C.danger};
    word-break: keep-all;
`;

interface Attached {
    token: string;
    name: string;
    previewUrl: string;
}

const BusinessCertPhotoField = ({ upload, onChange, error, existingNote, blockedReason, onBlocked }: BusinessCertPhotoFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const seqRef = useRef<number>(0);
    const [attached, setAttached] = useState<Attached | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [localError, setLocalError] = useState<string>('');

    // 미리보기 주소는 바뀌거나 사라질 때 놓아 준다
    useEffect(() => () => {
        if (attached?.previewUrl) URL.revokeObjectURL(attached.previewUrl);
    }, [attached]);

    const open = () => {
        if (uploading) return;
        if (blockedReason) {
            setLocalError(blockedReason);
            onBlocked?.();
            return;
        }
        inputRef.current?.click();
    };

    const pick = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file: File | undefined = event.target.files?.[0];
        // 같은 파일을 다시 고를 수 있게 비운다
        event.target.value = '';
        if (!file) return;
        const typeProblem: string | null = businessCertFileProblem(file, 'type');
        if (typeProblem) {
            setLocalError(typeProblem);
            return;
        }
        const seq: number = ++seqRef.current;
        setUploading(true);
        setLocalError('');
        try {
            const prepared: File = await compressLicenseImage(file);
            const sizeProblem: string | null = businessCertFileProblem(prepared, 'size');
            if (sizeProblem) {
                if (seq === seqRef.current) setLocalError(sizeProblem);
                return;
            }
            const res = await upload(prepared);
            if (seq !== seqRef.current) return;
            const token: string | undefined = res.result?.token;
            if (!token) {
                setLocalError(res.message || '사진을 올리지 못했어요. 다시 시도해 주세요.');
                return;
            }
            setAttached({ token, name: prepared.name, previewUrl: prepared.type === 'application/pdf' ? '' : URL.createObjectURL(prepared) });
            onChange(token);
        } catch (e: any) {
            if (seq === seqRef.current) setLocalError(e?.response?.data?.message || '사진을 올리지 못했어요. 다시 시도해 주세요.');
        } finally {
            if (seq === seqRef.current) setUploading(false);
        }
    };

    const remove = () => {
        seqRef.current += 1;
        setUploading(false);
        setAttached(null);
        onChange(null);
    };

    const shownError: string = localError || error || '';
    return (
        <Wrap data-field="businessCert">
            {!attached && (
                <PickButton type="button" onClick={open} disabled={uploading} aria-label="사업자등록증 사진 올리기">
                    {uploading ? <ButtonSpinner /> : existingNote ? '사업자등록증 다시 올리기' : '사업자등록증 사진 올리기'}
                </PickButton>
            )}
            {attached && (
                <AttachedBox>
                    {attached.previewUrl && <Preview src={attached.previewUrl} alt="올린 사업자등록증 미리보기" />}
                    <AttachedRow>
                        <span>{attached.name}</span>
                        <RemoveButton type="button" onClick={remove}>삭제</RemoveButton>
                    </AttachedRow>
                </AttachedBox>
            )}
            {!attached && existingNote && <Help>{existingNote}</Help>}
            {shownError && <ErrorText role="alert">{shownError}</ErrorText>}
            <Help>글자가 잘 보이게 찍어 주세요. 사진은 치과 확인에만 쓰고 30일 뒤 지워요.</Help>
            <input ref={inputRef} type="file" accept={BUSINESS_CERT_MIME.join(',')} hidden onChange={pick} />
        </Wrap>
    );
};

export default BusinessCertPhotoField;
